/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ModuleViewMoreDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState } from "react";
import CustomDialogBoxV2 from "../../../common/customDialogBox/CustomDialogBoxV2";
import { Box, Button } from "@mui/material";
import CustomTabsV2 from "../../../common/tabs/CustomTabsV2";
import ModuleViewMore from "./ModuleViewMore";
import ViewUploadedFile from "../../../../file-upload/ViewUploadedFile";
import { useDispatch, useSelector } from "react-redux";
import { HandleApiActions, labCar } from "../../../../../redux/actions";
import ComponentFunction from "./ComponentFunction";
import CustomToolTip from "../../../../infoButton/CustomToolTip";
import CustomSearchFilterV2 from "../../../common/searchBar/CustomSearchFilterV2";
import CustomPaginationV2 from "../../../common/pagination/CustomPaginationV2";

function ModuleViewMoreDialog({
  isEEDialogOpen,
  toggleEEDialog,
  selectedComponent,
  linkApiProps,
  apiAction,
  apiDataKey,
  type,
  inline = false,
  projectId,
  variantId,
}) {
  const dispatch = useDispatch();
  const selectedProject = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariant = useSelector(
    (state) => state?.projectConfigReducer?.selectedVariantDetails,
  );
  const resolvedProjectId = projectId ?? selectedProject?.project_id;
  const resolvedVariantId = variantId ?? selectedVariant?.variant_id;

  const [linkedComponent, setLinkedComponent] = useState([]);
  const [componentSelfData, setComponentSelfData] = useState({});
  const [componentFunction, setComponentFunction] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [filteredAttributes, setFilteredAttributes] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");

  const restrictedTab = {
    EE: [],
    SWM: [],
    VS: [0],
    VC: [0],
  };

  const getDefaultTab = () => {
    for (let i = 0; i < 3; i++) {
      let restrictedArr = restrictedTab[type] || [];
      if (!restrictedArr.includes(i)) {
        return i;
      }
    }
    return 0;
  };
  const [viewMoreTabValue, setViewMoreTabValue] = useState(getDefaultTab());

  useEffect(() => {
    setViewMoreTabValue(getDefaultTab());
  }, [type]);

  const getComponentApiByType = () => {
    console.log("---getComponentApiByType---", type);
    switch (type) {
      case "EE":
        return {
          apiAction: labCar?.get_ee_components,
          // componentFunctionKey: "vehicle_system_details",
          tabName: "Software Modules",
          idKey: "ee_id",
          // EE: filter linked list down to only those IDs recorded on the ticket
          linkedComponentsForTickets:
            selectedComponent?.ee_details?.linked_ee_ids?.map((item) =>
              Number(item),
            ) || [],
          // null means "no filter — show everything returned by the API"
          linkedComponentsFilterEnabled: true,

          headder: {
            name: componentSelfData.assembly_number || "-",
            version: componentSelfData.version || "-",
            description: componentSelfData.description || "-",
            supplier: componentSelfData.supplier_name || "-",
          },

          childApiAction: labCar?.get_sw_modules,
          childKey: "description",
          searchKeys: ["description"],

        };
      case "SWM":
        return {
          apiAction: labCar?.get_sw_modules,
          componentFunctionKey: "vehicle_functions",
          tabName: "Vehicle Functions",
          idKey: "swm_id",
          // SWM: filter linked list down to only those IDs recorded on the ticket
          linkedComponentsForTickets:
            selectedComponent?.swm_details?.linked_swm_ids?.map((item) =>
              Number(item),
            ) || [],
          linkedComponentsFilterEnabled: true,

          headder: {
            name: componentSelfData.name || "-",
            version: componentSelfData.version || "-",
            description: componentSelfData.description || "-",
            supplier: componentSelfData.supplier_name || "-",
          },

          childApiAction: labCar?.get_sw_modules,
          childKey: "vehicle_functions",
          searchKeys: ["name", "abbreviation"],

        };

      case "VS":
        return {
          apiAction: labCar?.get_vehicle_systems,
          // componentFunctionKey: "vehicle_functions",
          tabName: "Vehicle Components",
          idKey: "id",
          // VS: no per-ticket ID filter — show ALL linked items returned by the
          // API, identical to what "View More" shows in the Vehicle System table.
          // linkedComponentsForTickets:
          //   selectedComponent?.swm_details?.linked_swm_ids?.map((item) =>
          //     Number(item),
          //   ) || [],
          linkedComponentsForTickets: [],
          linkedComponentsFilterEnabled: false,

          headder: {
            name: componentSelfData.name || "-",
            version: componentSelfData.assembly_number || "-",
            description: componentSelfData.description || "-",
            // supplier: componentSelfData.supplier_name || "-",
            supplier: selectedComponent?.supplier_name || "-",
          },

          childApiAction: labCar?.get_vehicle_components,
          childKey: "description",
          searchKeys: ["name", "component_number", "description"],

        };

      case "VC":
        return {
          apiAction: labCar?.get_vehicle_components,
          // componentFunctionKey: "vehicle_functions",
          tabName: "EE Components",
          idKey: "id",
          // VC: no per-ticket ID filter — show ALL linked items returned by the
          // API, identical to what "View More" shows in the Vehicle Component table.
          // linkedComponentsForTickets:
          //   selectedComponent?.swm_details?.linked_swm_ids?.map((item) =>
          //     Number(item),
          //   ) || [],
          linkedComponentsForTickets: [],
          linkedComponentsFilterEnabled: false,

          headder: {
            name: componentSelfData.name || "-",
            version: componentSelfData.component_number || "-",
            description: componentSelfData.description || "-",
            // supplier: componentSelfData.supplier_name || "-",
            supplier: selectedComponent?.supplier_name || "-",
          },
          // child keys
          childApiAction: labCar?.get_ee_components,
          childKey: "description",
          searchKeys: [
            "assembly_number",
            "description",
            "supplier_name",
            "version",
          ],
        };
      default:
        return {};
    }
  };

  const getComponentDetails = async () => {
    try {
      console.log(
        "---getComponentDetails--",
        getComponentApiByType()?.apiAction,
      );
      const resp = await dispatch(
        HandleApiActions({
          ...getComponentApiByType()?.apiAction,
          params: {
            ...linkApiProps,
            project_id: resolvedProjectId,
            variant_id: resolvedVariantId,
            page_number: page,
            page_size: rowsPerPage,
          },
          show_toast: false,
        }),
      );

      if (resp?.error) {
        setComponentSelfData({});
        setAttributes([]);
        setFilteredAttributes([]);
        setPage(1);
      } else {
        console.log("resp?.data?.[0]", resp?.data?.[0]);
        setComponentSelfData(resp?.data?.[0]);

        const attrs = resp?.data?.[0]?.attributes || [];
        setAttributes(attrs);
        setFilteredAttributes(attrs);
        setPage(1);
      }

      let childResp = await dispatch(
        HandleApiActions({
          ...getComponentApiByType()?.childApiAction,
          params: {
            ...linkApiProps,
            project_id: resolvedProjectId,
            variant_id: resolvedVariantId,
            page_number: page,
            page_size: rowsPerPage,
          },
          show_toast: false,
        }),
      );

      console.log("---childResp---", childResp);

      if (childResp?.error) {
        setComponentFunction([]);
      } else {
        if (type === "SWM") {
          setComponentFunction(
            childResp?.data?.[0]?.[getComponentApiByType()?.childKey] || [],
          );
        } else {
          setComponentFunction(childResp?.data);
        }
      }
    } catch (error) {
      console.error("Failed to fetch component details:", error);
      setComponentSelfData({});
      setComponentFunction([]);
      setAttributes([]);
      setFilteredAttributes([]);
      setPage(1);
    }
  };

  const getLinkedComponents = async () => {
    try {
      const apiDetails = apiAction;
      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            ...linkApiProps,
            project_id: resolvedProjectId,
            variant_id: resolvedVariantId,
          },
          show_toast: false,
        }),
      );

      const { linkedComponentsFilterEnabled, linkedComponentsForTickets, idKey } =
        getComponentApiByType();

      console.log(
        "---linkedComponent response---",
        linkedComponentsForTickets,
        resp?.data?.[0]?.[apiDataKey],
      );

      if (resp?.error) {
        setLinkedComponent([]);
      } else {
        const allLinked = resp?.data?.[0]?.[apiDataKey] || [];

        // For EE and SWM: filter to only IDs explicitly associated with the
        // ticket. For VS and VC: show all linked items as returned by the API
        // (matching the existing "View More" dialog behaviour).
        const filtered = linkedComponentsFilterEnabled
          ? allLinked.filter((element) =>
            linkedComponentsForTickets.includes(Number(element[idKey])),
          )
          : allLinked;

        console.log("---filtered linkedComponent---", filtered);

        setLinkedComponent(
          filtered.map((item) => ({
            ...item,
            network: item.network_interface,
          })),
        );
      }
    } catch (error) {
      console.error("Failed to fetch linked components:", error);
      setLinkedComponent([]);
    }
  };

  useEffect(() => {
    if (!inline && !isEEDialogOpen) {
      return;
    }

    getComponentDetails();
    getLinkedComponents();
  }, [
    inline,
    isEEDialogOpen,
    selectedComponent,
    linkApiProps,
    apiAction,
    apiDataKey,
    type,
    resolvedProjectId,
    resolvedVariantId,
  ]);

  // Useeffect to make empty array in redux when cleanup
  useEffect(() => {
    if (!inline && !isEEDialogOpen) {
      const actionType = labCar?.get_ee_components?.action_type;
      console.log("----cleanup redux actionType: ", actionType);
      if (actionType) {
        dispatch({
          type: actionType,
          payload: {
            data: [],
          },
        });
      }
    }
  }, [inline, isEEDialogOpen, dispatch]);

  // const renderHeader = () => (
  //   // <Box
  //   //   sx={{
  //   //     fontSize: "14px",
  //   //     fontWeight: "bold",
  //   //     color: "#67696d",
  //   //   }}
  //   // >
  //   //   <Box sx={{ display: "flex", gap: 1 }}>
  //   //     {type === "VS" || type === "VC" ? "Name:" : "Assembly No:"}{" "}
  //   //     <CustomToolTip length={80}>
  //   //       {getComponentApiByType()?.headder?.name}
  //   //     </CustomToolTip>
  //   //   </Box>
  //   //   <Box sx={{ display: "flex", gap: 1 }}>
  //   //     {type === "VS"
  //   //       ? "Assembly Number:"
  //   //       : type === "VC"
  //   //         ? "Component Number:"
  //   //         : "Version:"}
  //   //     <CustomToolTip length={80}>
  //   //       {getComponentApiByType()?.headder?.version}
  //   //     </CustomToolTip>
  //   //   </Box>
  //   //   <Box sx={{ display: "flex", gap: 1 }}>
  //   //     Description:{" "}
  //   //     <CustomToolTip length={80}>
  //   //       {getComponentApiByType()?.headder?.description}
  //   //     </CustomToolTip>
  //   //   </Box>
  //   // </Box>
  // );

  const renderContent = () => (
    <Box sx={{ height: inline ? "100%" : "450px" }}>
      {console.log(
        "---View more linked component---",
        linkedComponent,
        getComponentApiByType()?.linkedComponentsForTickets,
      )}
      <CustomTabsV2
        displayZero={true}
        value={viewMoreTabValue}
        defaultTab={getDefaultTab()}
        onTabChange={(newTab) => {
          if (!restrictedTab[type].includes(newTab)) {
            setViewMoreTabValue(newTab);
          }
        }}
        tabs={[
          {
            name: "Linked Component",
            count: linkedComponent?.length || 0,
            content: (
              <ModuleViewMore
                linkedComponent={linkedComponent}
                linkedComponentsForTickets={
                  getComponentApiByType()?.linkedComponentsForTickets
                }
                setLinkedComponent={setLinkedComponent}
                type={type}
              />
            ),
          },
          {
            name: getComponentApiByType()?.tabName,
            count: componentFunction?.length || 0,
            content: (
              <ComponentFunction
                type={type}
                componentFunction={componentFunction}
                searchKeys={getComponentApiByType()?.searchKeys}
              />
            ),
          },
          {
            name: "Attributes",
            count: attributes?.length || 0,
            content: (
              <Box>
                <Box mb={1}>
                  <CustomSearchFilterV2
                    placeholder={"Search"}
                    searchKeys={["attribute_heading", "attribute_value"]}
                    getDataOnSearch={(filteredData) => {
                      setFilteredAttributes(filteredData);
                      setPage(1);
                    }}
                    inputData={attributes}
                  />
                </Box>
                <Box
                  sx={{
                    width: inline ? "100%" : "750px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Box sx={{ height: "250px", overflow: "auto" }}>
                    {filteredAttributes && filteredAttributes.length > 0 ? (
                      filteredAttributes
                        .slice((page - 1) * rowsPerPage, page * rowsPerPage)
                        .map((attr) => (
                          <Box
                            key={attr.id}
                            sx={{
                              padding: "10px",
                              // marginBottom: "12px",
                              border: "1px solid #e0e0e0",
                              // borderRadius: "4px",
                              // backgroundColor: "#f9f9f9",
                              // "&:hover": {
                              //   backgroundColor: "#f5f5f5",
                              // },
                              textAlign: "left",
                            }}
                          >
                            <Box
                              sx={{
                                fontWeight: "600",
                                fontSize: "14px",
                                color: "#333",
                                marginBottom: "4px",
                              }}
                            >
                              {attr.attribute_heading}
                            </Box>
                            <Box
                              sx={{
                                fontSize: "13px",
                                color: "#666",
                              }}
                            >
                              {attr.attribute_type === "FILE" ? (
                                <Button
                                  size="small"
                                  variant="text"
                                  disabled={!attr.attribute_value}
                                  onClick={() => {
                                    setSelectedFile(attr.attribute_value);
                                    setSelectedFileName(attr.attribute_heading);
                                    setOpenPreview(true);
                                  }}
                                  sx={{
                                    textTransform: "none",
                                    whiteSpace: "nowrap",
                                    p: 0,
                                    minWidth: 0,
                                    justifyContent: "flex-start",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  View
                                </Button>
                              ) : (
                                attr.attribute_value
                              )}
                            </Box>
                          </Box>
                        ))
                    ) : (
                      <Box
                        sx={{
                          padding: "16px",
                          textAlign: "center",
                          color: "#999",
                        }}
                      >
                        No attributes available
                      </Box>
                    )}
                  </Box>

                  {filteredAttributes && filteredAttributes.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        borderTop: "1px solid #e0e0e0",
                      }}
                    >
                      <CustomPaginationV2
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={(pageNo) => {
                          setPage(pageNo);
                        }}
                        totalItems={filteredAttributes.length}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            ),
          },
        ]}
      />
    </Box>
  );

  if (inline) {
    return (
      <>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {/* <Box sx={{ borderBottom: "1px solid #e0e0e0", pb: 1 }}>
            {renderHeader()}
          </Box> */}
          {renderContent()}
        </Box>
        <ViewUploadedFile
          open={openPreview}
          onClose={() => {
            setOpenPreview(false);
            setSelectedFile(null);
          }}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          downloadFile={true}
          fileName={selectedFileName}
        />
      </>
    );
  }

  return (
    <>
      <CustomDialogBoxV2
        title={
          <Box
            sx={{
              fontSize: "14px",
              fontWeight: "bold",
              color: "#67696d",
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              {type === "VS" || type === "VC" ? "Name:" : "Assembly No:"}{" "}
              <CustomToolTip length={80}>
                {getComponentApiByType()?.headder?.name}
              </CustomToolTip>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {type === "VS"
                ? "Assembly Number:"
                : type === "VC"
                  ? "Component Number:"
                  : "Version:"}
              <CustomToolTip length={80}>
                {getComponentApiByType()?.headder?.version}
              </CustomToolTip>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              Description:{" "}
              <CustomToolTip length={80}>
                {getComponentApiByType()?.headder?.description}
              </CustomToolTip>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              Supplier Name:{" "}
              <CustomToolTip length={80}>
                {getComponentApiByType()?.headder?.supplier}
              </CustomToolTip>
            </Box>
          </Box>
        }
        open={isEEDialogOpen}
        onClose={toggleEEDialog(false)}
      >
        {renderContent()}
      </CustomDialogBoxV2>
      <ViewUploadedFile
        open={openPreview}
        onClose={() => {
          setOpenPreview(false);
          setSelectedFile(null);
        }}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        downloadFile={true}
        fileName={selectedFileName}
      />
    </>
  );
}

export default ModuleViewMoreDialog;